/**
 * The Squares Game tab (ISSUE-1270, screens 14, 15, 16). Mounted by
 * `features/poolScreen/poolTypePanes.tsx` with this props shape.
 *
 * Top to bottom: the game picker card (the grid list for Classic, the run of
 * games for Shared), the status row (Grid Status chip, then Manage Grid for
 * admins or "My squares 3 of 5" for members), the server's caption, the
 * board, and the Winning squares card. The board stays fresh by polling while
 * the tab is visible and the grid is open, locked or live (ISSUE-1427), and
 * a take that loses a race refetches at once through the hook's invalidation.
 *
 * Who may tap which square, the numbers, the limit and every winner come from
 * contest-api. This pane renders them.
 */
import {
  useSquaresGridGet,
  useSquaresPoolGet,
  useSquaresSquareRelease,
  useSquaresSquareTake,
} from "@sportsmet/contest-sdk/private";
import { Button, Card, InlineBanner, Skeleton } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";

import type { typePoolTypePaneProps } from "../poolScreen/poolTypePaneProps";
import { resolveViewerTimeZone } from "../scores/viewerTimeZone";
import {
  runSquaresMutation,
  useRefreshSquaresPoolLine,
} from "./squaresActions";
import { SquaresAddWinnerSheet } from "./SquaresAddWinnerSheet";
import { SquaresAssignSheet } from "./SquaresAssignSheet";
import { SquaresBoard } from "./SquaresBoard";
import {
  SQUARES_EMPTY_NO_SEASON,
  SQUARES_GAME_ERROR,
  SQUARES_GAME_LOADING,
  SQUARES_GAME_TRY_AGAIN,
  SQUARES_GRID_LIST_DRAFT_NOTE,
  SQUARES_GRID_LIST_TITLE,
  SQUARES_MANAGE_ASSIGN_HINT,
  SQUARES_MANAGE_GRID,
  SQUARES_PICK_GRID_LABEL,
  SQUARES_TAKE_BLOCKED,
  squaresEmptyAwaitingSchedule,
  squaresGameOfLine,
  squaresGridOfLine,
  squaresGridStatusLabel,
  squaresMySquaresLine,
} from "./squaresCopy";
import { takeSquaresFocus } from "./squaresFocusStore";
import { SquaresManageGridSheet } from "./SquaresManageGridSheet";
import {
  buildSquaresBoardLayout,
  squaresBoardPollMs,
  squaresBoardSignature,
  squaresEventStatusLine,
  squaresAxisName,
  squaresHasGameScore,
  squaresKickoffLabel,
  squaresMatchupLabel,
  squaresScoreLine,
  type typeSquaresBoardCell,
} from "./squaresModel";
import { SquaresPickerSheet } from "./SquaresPickerSheet";
import { SquaresStatusChip } from "./SquaresStatusChip";
import type {
  typeSquaresEvent,
  typeSquaresGridSummary,
  typeSquaresWinner,
} from "./squaresTypes";
import { SquaresWinningCard } from "./SquaresWinningCard";
import { useSquaresLeagueTeams } from "./useSquaresLeagueTeams";
import { useSquaresOpenPoolTab } from "./useSquaresOpenPoolTab";

type typeSheet = "grids" | "events" | "manage" | "addWinner" | null;

const ChevronIcon = (): ReactNode => (
  <svg
    className="h-icon-md w-icon-md shrink-0 text-ink-secondary"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
    focusable="false"
  >
    <path
      d="m6 8 4 4 4-4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SquaresGameTab = ({
  poolId,
}: typePoolTypePaneProps): ReactNode => {
  const [focus] = useState(() => takeSquaresFocus(poolId));
  const [chosenGridId, setChosenGridId] = useState<string | null>(
    focus?.gridId ?? null,
  );
  const [chosenEventId, setChosenEventId] = useState<string | null>(
    focus?.eventId ?? null,
  );
  const [focusKey, setFocusKey] = useState<string | null>(
    focus?.squareKey ?? null,
  );
  const [sheet, setSheet] = useState<typeSheet>(null);
  const [assignCell, setAssignCell] = useState<typeSquaresBoardCell | null>(
    null,
  );
  const [isAssignHintShown, setAssignHintShown] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const openPoolTab = useSquaresOpenPoolTab();
  const refreshPoolLine = useRefreshSquaresPoolLine(poolId);
  const timeZone = resolveViewerTimeZone();

  const poolQuery = useSquaresPoolGet({ poolId });
  const pool = poolQuery.data?.data ?? null;
  const season = pool?.season ?? null;
  const teams = useSquaresLeagueTeams(season?.leagueKey ?? null);
  const grids = pool?.grids ?? [];
  const gridId =
    chosenGridId !== null && grids.some((grid) => grid.gridId === chosenGridId)
      ? chosenGridId
      : (pool?.currentGridId ?? grids[0]?.gridId ?? null);

  const gridQuery = useSquaresGridGet(
    {
      poolId,
      gridId: gridId ?? "",
      ...(chosenEventId === null ? {} : { eventId: chosenEventId }),
    },
    {
      enabled: gridId !== null,
      refetchInterval: (query) =>
        squaresBoardPollMs(query.state.data?.data?.refreshIntervalMs),
    },
  );
  const take = useSquaresSquareTake();
  const release = useSquaresSquareRelease();

  // QA round 1 (ISSUE-1427). A poll that shows the board moved (someone took
  // a square from another device, or the grid locked) refreshes the reads
  // that repeat it: the pool header line and pool cards (pool-api), the grid
  // list's state, and the Assign sheet's counts. The first answer only sets
  // the baseline, so opening the tab costs no extra calls.
  const queryClient = useQueryClient();
  const boardSignature = squaresBoardSignature(gridQuery.data?.data);
  const lastBoardSignature = useRef<string | null>(null);
  useEffect(() => {
    if (boardSignature === null) return;
    const previous = lastBoardSignature.current;
    lastBoardSignature.current = boardSignature;
    if (previous === null || previous === boardSignature) return;
    for (const queryKey of [
      ["post:pool:poolGet", "svc:pool-api"],
      ["post:pool:poolListMine", "svc:pool-api"],
      ["post:squares:squaresPoolGet", "svc:contest-api"],
      ["post:squares:squaresAssigneeList", "svc:contest-api"],
    ]) {
      void queryClient.invalidateQueries({ queryKey });
    }
  }, [boardSignature, queryClient]);

  // A grid that locks while the Assign sheet is open closes the sheet: no
  // square can change on a locked grid, so it would only offer dead buttons.
  const polledGridState = gridQuery.data?.data?.grid.gridState ?? null;
  useEffect(() => {
    if (polledGridState !== null && polledGridState !== "open") {
      setAssignCell(null);
    }
  }, [polledGridState]);

  const formatEvent = (event: {
    awayTeamId: string;
    homeTeamId: string;
  }): string =>
    squaresMatchupLabel({
      awayTeamId: event.awayTeamId,
      homeTeamId: event.homeTeamId,
      teamsById: teams.teamsById,
    });
  /** A Classic grid's matchup from pool-get, or null before teams are known. */
  const gridSummaryMatchup = (item: typeSquaresGridSummary): string | null =>
    // ISSUE-1282. A custom game has no teams: its name stands in.
    (item.customTitle ?? null) !== null
      ? (item.customTitle ?? null)
      : item.homeTeamId === null ||
          item.homeTeamId === undefined ||
          item.awayTeamId === null ||
          item.awayTeamId === undefined
        ? null
        : formatEvent({
            awayTeamId: item.awayTeamId,
            homeTeamId: item.homeTeamId,
          });

  if (poolQuery.isLoading) {
    return (
      <div
        className="flex flex-col gap-3"
        data-testid="squares-game-tab"
        aria-label={SQUARES_GAME_LOADING}
      >
        <Skeleton className="h-10" />
        <Skeleton className="aspect-square w-full" />
      </div>
    );
  }
  if (pool === null) {
    return (
      <div className="flex flex-col gap-3" data-testid="squares-game-tab">
        <InlineBanner
          variant="warning"
          message={SQUARES_GAME_ERROR}
          action={{
            label: SQUARES_GAME_TRY_AGAIN,
            onAction: () => {
              void poolQuery.refetch();
            },
          }}
        />
      </div>
    );
  }
  if (season === null || pool.emptyState === "noSeason") {
    return (
      <div className="flex flex-col gap-3" data-testid="squares-game-tab">
        <Card data-testid="squares-game-empty">
          <p className="text-body text-ink-secondary">
            {SQUARES_EMPTY_NO_SEASON}
          </p>
        </Card>
      </div>
    );
  }
  if (gridId === null || pool.emptyState === "awaitingSchedule") {
    return (
      <div className="flex flex-col gap-3" data-testid="squares-game-tab">
        <Card data-testid="squares-game-empty">
          <p className="text-body text-ink-secondary">
            {squaresEmptyAwaitingSchedule(teams.leagueName)}
          </p>
        </Card>
      </div>
    );
  }

  const data = gridQuery.data?.data ?? null;
  const summary = grids.find((grid) => grid.gridId === gridId) ?? null;
  const canManage = pool.viewer.canManage;
  const grid = data?.grid ?? null;
  const selectedEventId =
    data?.selectedEventId ?? chosenEventId ?? data?.events[0]?.eventId ?? null;
  const selectedEvent: typeSquaresEvent | null =
    data?.events.find((event) => event.eventId === selectedEventId) ??
    data?.events[0] ??
    null;
  const isShared = grid?.gridType === "shared";
  const gridIndex = grids.findIndex((item) => item.gridId === gridId);

  // ISSUE-1270. Both types lead with the matchup ("Bears at Seahawks"), then
  // the week or round and where this grid or game sits in the run.
  const summaryMatchup = summary === null ? null : gridSummaryMatchup(summary);
  const pickerTitle =
    selectedEvent === null
      ? (summaryMatchup ??
        grid?.customTitle ??
        summary?.label ??
        grid?.label ??
        "")
      : formatEvent(selectedEvent);
  const pickerRoundLabel =
    selectedEvent?.roundLabel ??
    (isShared ? null : summary?.roundLabel) ??
    null;
  const pickerParts: string[] = [];
  if (pickerRoundLabel !== null && pickerRoundLabel !== "") {
    pickerParts.push(pickerRoundLabel);
  }
  if (isShared && data !== null && selectedEvent !== null) {
    pickerParts.push(
      squaresGameOfLine({
        index:
          data.events.findIndex(
            (event) => event.eventId === selectedEvent.eventId,
          ) + 1,
        total: data.gameCount,
      }),
    );
  } else if (!isShared && grids.length > 1) {
    pickerParts.push(
      squaresGridOfLine({ sequence: gridIndex + 1, total: grids.length }),
    );
  }
  const scoreLine =
    selectedEvent === null
      ? null
      : squaresScoreLine({ ...selectedEvent, teamsById: teams.teamsById });
  const statusLine =
    selectedEvent === null ? null : squaresEventStatusLine(selectedEvent);
  const kickoffLine =
    selectedEvent === null || statusLine !== null
      ? null
      : squaresKickoffLabel({ kickoffAt: selectedEvent.kickoffAt, timeZone });
  const isPickerStatic = isShared
    ? (data?.events.length ?? 0) <= 1
    : grids.length <= 1;

  const isOpen = grid?.gridState === "open";
  const viewer = data?.viewer ?? null;

  function isCellEnabled(cell: typeSquaresBoardCell): boolean {
    if (!isOpen || viewer === null) return false;
    if (canManage) return true;
    if (cell.isMine) return true;
    return cell.square === null && viewer.canTake;
  }

  async function pressCell(cell: typeSquaresBoardCell): Promise<void> {
    if (gridId === null) return;
    setFocusKey(null);
    if (canManage) {
      setAssignHintShown(false);
      setAssignCell(cell);
      return;
    }
    const body = { poolId, gridId, squareKey: cell.squareKey };
    setBusyKey(cell.squareKey);
    await runSquaresMutation(
      () => (cell.isMine ? release.mutateAsync(body) : take.mutateAsync(body)),
      refreshPoolLine,
    );
    setBusyKey(null);
  }

  const axisName = (side: "top" | "side", isShort: boolean): string =>
    grid === null
      ? ""
      : squaresAxisName({
          axesKind: grid.axes.kind,
          label: side === "top" ? grid.axes.topLabel : grid.axes.sideLabel,
          teamId: side === "top" ? grid.axes.topTeamId : grid.axes.sideTeamId,
          teamsById: teams.teamsById,
          isShort,
        });

  const winnersForEvent = (data?.winners ?? []).filter(
    (winner) => selectedEventId === null || winner.eventId === selectedEventId,
  );
  const formatWinnerScore = (winner: typeSquaresWinner): string => {
    // ISSUE-1282. A custom game has no score to show.
    if ((grid?.customTitle ?? null) !== null) return "";
    const event = data?.events.find((item) => item.eventId === winner.eventId);
    if (event !== undefined && !squaresHasGameScore(event.status)) return "";
    if (event === undefined) {
      return `${String(winner.awayScore)} · ${String(winner.homeScore)}`;
    }
    return (
      squaresScoreLine({
        awayTeamId: event.awayTeamId,
        homeTeamId: event.homeTeamId,
        awayScore: winner.awayScore,
        homeScore: winner.homeScore,
        teamsById: teams.teamsById,
      }) ?? ""
    );
  };
  const gameLabel = [
    selectedEvent === null ? pickerTitle : formatEvent(selectedEvent),
    selectedEvent?.roundLabel ?? null,
  ]
    .filter((part): part is string => part !== null && part !== "")
    .join(" · ");

  const pickerBody = (
    <span className="flex w-full items-center justify-between gap-3">
      <span className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-body-strong text-ink">
          {pickerTitle}
        </span>
        {pickerParts.length === 0 ? null : (
          <span className="text-caption text-ink-secondary">
            {pickerParts.join(" · ")}
          </span>
        )}
        {scoreLine === null &&
        statusLine === null &&
        kickoffLine === null ? null : (
          <span
            className="text-caption text-ink"
            data-testid="squares-picker-score"
          >
            {[scoreLine, statusLine, kickoffLine]
              .filter((part): part is string => part !== null && part !== "")
              .join(" · ")}
          </span>
        )}
      </span>
      {isPickerStatic ? null : <ChevronIcon />}
    </span>
  );

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="squares-game-tab"
      data-pool-id={poolId}
    >
      {isPickerStatic ? (
        <Card padding="compact" data-testid="squares-game-picker">
          {pickerBody}
        </Card>
      ) : (
        <Card
          padding="compact"
          data-testid="squares-game-picker"
          aria-label={`${SQUARES_PICK_GRID_LABEL}: ${pickerTitle}`}
          onClick={() => {
            setSheet(isShared ? "events" : "grids");
          }}
        >
          {pickerBody}
        </Card>
      )}

      {grid === null || data === null ? (
        gridQuery.isError ? (
          <InlineBanner
            variant="warning"
            message={SQUARES_GAME_ERROR}
            action={{
              label: SQUARES_GAME_TRY_AGAIN,
              onAction: () => {
                void gridQuery.refetch();
              },
            }}
          />
        ) : (
          <div
            className="flex flex-col gap-3"
            aria-label={SQUARES_GAME_LOADING}
          >
            <Skeleton className="h-10" />
            <Skeleton className="aspect-square w-full" />
          </div>
        )
      ) : (
        <>
          <div
            className="flex items-center justify-between gap-3"
            data-testid="squares-status-row"
          >
            <SquaresStatusChip
              gridState={grid.gridState}
              publishStatus={grid.publishStatus}
            />
            {canManage ? (
              <Button
                variant="secondary"
                size="sm"
                data-testid="squares-manage-grid"
                onClick={() => {
                  setSheet("manage");
                }}
              >
                {SQUARES_MANAGE_GRID}
              </Button>
            ) : (
              <span
                className="text-caption font-semibold text-ink"
                data-testid="squares-my-squares"
              >
                {squaresMySquaresLine({
                  count: data.viewer.mySquareCount,
                  max: data.viewer.maxPerMember ?? null,
                })}
              </span>
            )}
          </div>
          {isAssignHintShown ? (
            <InlineBanner
              message={SQUARES_MANAGE_ASSIGN_HINT}
              onDismiss={() => {
                setAssignHintShown(false);
              }}
            />
          ) : null}
          {!canManage &&
          isOpen &&
          !data.viewer.canTake &&
          data.viewer.takeBlockedReason !== null &&
          data.viewer.takeBlockedReason !== undefined &&
          data.viewer.takeBlockedReason !== "gridClosed" ? (
            <p
              className="text-caption text-ink-secondary"
              data-testid="squares-take-blocked"
            >
              {SQUARES_TAKE_BLOCKED[data.viewer.takeBlockedReason]}
            </p>
          ) : null}
          <SquaresBoard
            layout={buildSquaresBoardLayout({ data, selectedEventId })}
            topLabel={axisName("top", false)}
            sideLabel={axisName("side", false)}
            focusKey={focusKey}
            busyKey={busyKey}
            isCellEnabled={isCellEnabled}
            onCellPress={(cell) => {
              void pressCell(cell);
            }}
          />
          <p
            className="text-caption text-ink-secondary"
            data-testid="squares-board-caption"
          >
            {grid.caption}
          </p>
          <SquaresWinningCard
            checkpointRows={data.checkpointRows}
            winners={winnersForEvent}
            topLabel={axisName("top", true)}
            sideLabel={axisName("side", true)}
            formatScore={formatWinnerScore}
            canManage={canManage}
            onAddWinner={() => {
              setSheet("addWinner");
            }}
            allWinnersCount={isShared ? data.winners.length : null}
            onOpenResults={
              isShared
                ? () => {
                    openPoolTab("results");
                  }
                : undefined
            }
          />
          {canManage ? (
            <SquaresManageGridSheet
              open={sheet === "manage"}
              onClose={() => {
                setSheet(null);
              }}
              poolId={poolId}
              grid={grid}
              otherGrids={grids
                .filter((item) => item.gridId !== grid.gridId)
                .map((item) => {
                  const matchup = gridSummaryMatchup(item);
                  return matchup === null
                    ? item
                    : { ...item, label: `${item.label} · ${matchup}` };
                })}
              winners={data.winners}
              lockLabel={
                grid.lockAt === null || grid.lockAt === undefined
                  ? null
                  : squaresKickoffLabel({ kickoffAt: grid.lockAt, timeZone })
              }
              onAddWinner={() => {
                setSheet("addWinner");
              }}
              onStartAssign={() => {
                setSheet(null);
                setAssignHintShown(true);
              }}
            />
          ) : null}
          {canManage ? (
            <SquaresAddWinnerSheet
              open={sheet === "addWinner"}
              onClose={() => {
                setSheet(null);
              }}
              poolId={poolId}
              gridId={grid.gridId}
              eventId={selectedEventId}
              formatEvent={formatEvent}
            />
          ) : null}
          {canManage ? (
            <SquaresAssignSheet
              poolId={poolId}
              gridId={grid.gridId}
              cell={assignCell}
              gameLabel={gameLabel}
              onClose={() => {
                setAssignCell(null);
              }}
            />
          ) : null}
          {isShared ? (
            <SquaresPickerSheet
              open={sheet === "events"}
              onClose={() => {
                setSheet(null);
              }}
              title={SQUARES_PICK_GRID_LABEL}
              value={selectedEventId}
              items={data.events.map((event) => ({
                value: event.eventId,
                label: formatEvent(event),
                description: [
                  event.roundLabel ?? null,
                  squaresEventStatusLine(event) ??
                    squaresKickoffLabel({
                      kickoffAt: event.kickoffAt,
                      timeZone,
                    }),
                ]
                  .filter(
                    (part): part is string => part !== null && part !== "",
                  )
                  .join(" · "),
              }))}
              onSelect={(eventId) => {
                setChosenEventId(eventId);
              }}
            />
          ) : null}
        </>
      )}
      {isShared ? null : (
        <SquaresPickerSheet
          open={sheet === "grids"}
          onClose={() => {
            setSheet(null);
          }}
          title={SQUARES_GRID_LIST_TITLE}
          note={canManage ? SQUARES_GRID_LIST_DRAFT_NOTE : undefined}
          value={gridId}
          items={grids.map((item) => {
            const matchup = gridSummaryMatchup(item);
            return {
              value: item.gridId,
              label: matchup ?? item.label,
              description: [
                matchup === null ? null : item.label,
                item.roundLabel ?? null,
                squaresGridStatusLabel(item),
                item.kickoffAt === null || item.kickoffAt === undefined
                  ? null
                  : squaresKickoffLabel({
                      kickoffAt: item.kickoffAt,
                      timeZone,
                    }),
                item.mySquareCount > 0
                  ? squaresMySquaresLine({
                      count: item.mySquareCount,
                      max: pool.viewer.maxPerMember ?? null,
                    })
                  : null,
              ]
                .filter((part): part is string => part !== null && part !== "")
                .join(" · "),
            };
          })}
          onSelect={(next) => {
            setChosenGridId(next);
            setChosenEventId(null);
            setFocusKey(null);
          }}
        />
      )}
    </div>
  );
};
